
const libPictSectionLeaflet = require('pict-section-leaflet');

/**
 * ProjectCenterMap — the geographic-center picker for the Project workspace. Extends the
 * pict-section-leaflet base but drives its own mount: the workspace calls showForProject() whenever the
 * Location tab becomes visible (Leaflet needs a visible, sized container), and the marker reports moves
 * back to the ProjectWorkspace provider's working center via a light update (so a marker drag never
 * re-renders the workspace and tears the map down under itself).
 */
class ProjectCenterMap extends libPictSectionLeaflet
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
		this._map = false;
		this._marker = false;
	}

	/** The host provider whose working center this map drives — 'ProjectWorkspace' by default, but a second
	 *  instance (ProjectCreateMap) points at 'ProjectCreateWorkspace'. Both expose center/hasCenter/setCenter. */
	get provider()
	{
		return this.pict.providers[(this.options && this.options.ProviderName) || 'ProjectWorkspace'];
	}

	/** The DOM id of the map host div (configurable so the create screen can use its own #PCW-MapHost). */
	get mapHostID()
	{
		return (this.options && this.options.MapHostID) || 'PW-MapHost';
	}

	get leafletLib()
	{
		return (typeof window !== 'undefined' && window.L) ? window.L : false;
	}

	/**
	 * (Re)mount the map into #PW-MapHost for the current project's center. Called on every Location-tab
	 * show; the panel host is recreated by the workspace render, so we tear down any prior map first.
	 */
	showForProject()
	{
		const L = this.leafletLib;
		const tmpHost = (typeof document !== 'undefined') ? document.getElementById(this.mapHostID) : null;
		if (!L || !tmpHost || !this.provider) { return; }

		if (this._map)
		{
			try { this._map.remove(); } catch (pError) { /* prior map already detached */ }
			this._map = false;
			this._marker = false;
		}

		const tmpCenter = this.provider.center;
		const tmpHasCenter = this.provider.hasCenter;
		// When there's no real center yet (unset, or the 0,0 "null island" sentinel), open on a wide
		// default view rather than dropping the user in the Gulf of Guinea.
		const tmpLat = tmpHasCenter ? Number(tmpCenter.Latitude) : 39.5;
		const tmpLng = tmpHasCenter ? Number(tmpCenter.Longitude) : -98.35;

		this._map = L.map(tmpHost).setView([ tmpLat, tmpLng ], tmpHasCenter ? 13 : 4);
		L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
			{ maxZoom: 19, attribution: '&copy; OpenStreetMap contributors' }).addTo(this._map);

		if (tmpHasCenter)
		{
			this._addMarker(tmpLat, tmpLng);
		}
		this._map.on('click', (pEvent) => { this._placeMarker(pEvent.latlng.lat, pEvent.latlng.lng); });

		// The container was just revealed; let Leaflet re-measure it on the next tick.
		setTimeout(() => { if (this._map) { this._map.invalidateSize(); } }, 60);
	}

	_addMarker(pLat, pLng)
	{
		const L = this.leafletLib;
		if (!L || !this._map) { return; }
		this._marker = L.marker([ pLat, pLng ], { draggable: true }).addTo(this._map);
		this._marker.on('dragend', (pEvent) =>
		{
			const tmpLatLng = pEvent.target.getLatLng();
			this._commit(tmpLatLng.lat, tmpLatLng.lng);
		});
	}

	_placeMarker(pLat, pLng)
	{
		if (!this._marker)
		{
			this._addMarker(pLat, pLng);
		}
		else
		{
			this._marker.setLatLng([ pLat, pLng ]);
		}
		this._commit(pLat, pLng);
	}

	/** Report the new center to the provider (light update: readout + Save only, map stays mounted). */
	_commit(pLat, pLng)
	{
		if (this.provider) { this.provider.setCenter(pLat, pLng); }
	}

	/**
	 * Move the marker + recenter the map to a programmatically supplied center (typed latitude/longitude
	 * or an address-search result). Unlike _placeMarker this does NOT _commit back to the provider — the
	 * provider's setCenter() already owns the working-center update, so committing here would loop.
	 * @param {Number} pLat @param {Number} pLng
	 */
	moveMarkerTo(pLat, pLng)
	{
		const tmpLat = Number(pLat);
		const tmpLng = Number(pLng);
		if (!this._map || !Number.isFinite(tmpLat) || !Number.isFinite(tmpLng)) { return; }
		if (!this._marker) { this._addMarker(tmpLat, tmpLng); }
		else { this._marker.setLatLng([ tmpLat, tmpLng ]); }
		this._map.setView([ tmpLat, tmpLng ], Math.max(this._map.getZoom() || 13, 13));
	}
}

module.exports = ProjectCenterMap;

module.exports.default_configuration = Object.assign({}, libPictSectionLeaflet.default_configuration,
	{
		ViewIdentifier: 'ProjectCenterMap',
		AutoRender: false,
		AutoInitialize: true,
		LeafletDestinationAddress: 'PW-MapHost',
		LeafletInitialCenter: [ 39.5, -98.35 ],
		LeafletInitialZoom: 4,
	});
